/**
 * DeliveryFunnelSection — expandable section on each blueprint card showing
 * the bid-request → impressions delivery funnel for the blueprint × campaign
 * × time-window combination.
 *
 * Scope (bd be0.5): full UI — stat strip + funnel band + drop annotations +
 * matched-URLs strip + footnote. The funnel band draws through the vendored
 * FunnelGraph wrapper (`ui/FunnelChart`) since the Brand Blueprints page lock
 * (deepcontext-pnxk) retired the CSS clip-path glass Sankey.
 *
 * be0.4 contributed the fetch wiring, loading skeleton, error panel, and
 * empty-state branches — those remain unchanged. be0.6 will wire the
 * `onOpenDomains` callback to the per-domain drill-down modal.
 */

import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {promptApi} from '../services/api';
import type {
    DeliveryFunnelDomainRow,
    DeliveryFunnelDrops,
    DeliveryFunnelLinkedCampaign,
    DeliveryFunnelPayload,
    DeliveryFunnelStage,
    DeliveryFunnelStatStrip,
    Diagnostic,
    DropReason,
} from '../types/deliveryFunnel';
import {formatCurrency, formatInteger, formatPercent} from '../utils/format';
import {formatCompactNumber, formatFullNumber} from '../utils/formatCompactNumber';
import FunnelChart from './ui/FunnelChart';

export interface DeliveryFunnelSectionProps {
    /** Blueprint (a.k.a. prompt) ID — UUID string. */
    blueprintId: string;
    /** Whether the section is expanded. Owned by the parent so multi-expand works. */
    isExpanded: boolean;
    /**
     * Campaigns linked to this blueprint. The bead spec says: if unavailable,
     * fall through to the "All campaigns" view (no campaign_id query param);
     * if explicitly empty, render the "no linked campaigns" empty state.
     */
    linkedCampaigns?: DeliveryFunnelLinkedCampaign[];
    /**
     * Optional callback fired when the user clicks the "which N domains here"
     * link inside the matched-URLs strip. Receives the domain rows from the
     * delivery-funnel payload plus the section's current `rangeDays` /
     * `campaignId` / `campaignName` so the drill-down modal (be0.6) can render
     * its header line ("Showing N domains … · {date range} · campaign {id}").
     * When provided, the link renders as a clickable button; when omitted, the
     * surrounding text still renders but the link portion is plain.
     */
    onOpenDomains?: (payload: DeliveryFunnelOpenDomainsPayload) => void;
}

/**
 * Payload handed to the `onOpenDomains` callback. The section owns the
 * date-range + campaign-selection state internally, so it includes them here;
 * otherwise the consuming parent would need to mirror the same state.
 */
export interface DeliveryFunnelOpenDomainsPayload {
    domains: DeliveryFunnelDomainRow[];
    /** Current date-range selector value, in days (7 | 14 | 30). */
    rangeDays: number;
    /** Currently selected campaign id, or null for "all campaigns". */
    campaignId: number | null;
    /** Human-readable campaign name when a campaign is selected. */
    campaignName?: string;
}

const DEFAULT_RANGE_DAYS = 7;

const RANGE_OPTIONS: Array<{value: number; label: string}> = [
    {value: 7, label: 'Last 7 days'},
    {value: 14, label: 'Last 14 days'},
    {value: 30, label: 'Last 30 days'},
];

function pad2(n: number): string {
    return n < 10 ? `0${n}` : `${n}`;
}

function isoFor(d: Date): string {
    return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

function dateRangeFor(days: number): {from: string; to: string} {
    const today = new Date();
    const to = isoFor(today);
    const fromDate = new Date(today.getTime());
    fromDate.setUTCDate(fromDate.getUTCDate() - (days - 1));
    return {from: isoFor(fromDate), to};
}

interface FetchState {
    status: 'idle' | 'loading' | 'success' | 'error';
    data: DeliveryFunnelPayload | null;
    errorMessage: string | null;
}

function initialFetchState(): FetchState {
    return {status: 'idle', data: null, errorMessage: null};
}

/**
 * Pulls a sensible human-readable error message out of an axios-style error.
 * Mirrors the error shape from the backend controller (be0.3): the body
 * is `{error: 'timeout'|'unavailable'|'auth'|'bad_input'|'not_found', message: string}`.
 */
function extractErrorMessage(err: unknown): string {
    // axios error → err.response.data.{message|error}
    const anyErr = err as {
        response?: {data?: {message?: string; error?: string}; status?: number};
        message?: string;
    };
    const body = anyErr?.response?.data;
    if (body) {
        if (typeof body.message === 'string' && body.message.length > 0) {
            return body.message;
        }
        if (typeof body.error === 'string' && body.error.length > 0) {
            return body.error;
        }
    }
    if (anyErr?.response?.status === 401) return 'Not authorized.';
    if (anyErr?.response?.status === 404) return 'Blueprint not found.';
    return anyErr?.message || 'Unknown error';
}

const SkeletonStatCell: React.FC = () => (
    <div
        data-testid="df-skeleton-stat"
        className="h-16 rounded-xl bg-line animate-pulse"
        aria-hidden
    />
);

const SkeletonGlass: React.FC = () => (
    <div
        data-testid="df-skeleton-glass"
        className="rounded-xl bg-line animate-pulse"
        style={{height: '190px'}}
        aria-hidden
    />
);

const SkeletonDropStrip: React.FC = () => (
    <div
        data-testid="df-skeleton-drop"
        className="h-6 rounded bg-line animate-pulse"
        aria-hidden
    />
);

const LoadingSkeleton: React.FC = () => (
    <div data-testid="df-loading" className="space-y-5" aria-busy="true" aria-live="polite">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <SkeletonStatCell />
            <SkeletonStatCell />
            <SkeletonStatCell />
            <SkeletonStatCell />
        </div>
        <div className="rounded-xl border border-line bg-surface p-4 space-y-3">
            <div className="h-4 w-1/3 bg-line animate-pulse rounded" aria-hidden />
            <SkeletonGlass />
            <div className="grid grid-cols-4 gap-1">
                <SkeletonStatCell />
                <SkeletonStatCell />
                <SkeletonStatCell />
                <SkeletonStatCell />
            </div>
            <div className="grid grid-cols-4 gap-1">
                <div />
                <SkeletonDropStrip />
                <SkeletonDropStrip />
                <SkeletonDropStrip />
            </div>
            <div className="h-10 rounded-md bg-line animate-pulse" aria-hidden />
        </div>
        <span className="sr-only">Loading delivery funnel…</span>
    </div>
);

interface ErrorPanelProps {
    message: string;
    onRetry: () => void;
}

const ErrorPanel: React.FC<ErrorPanelProps> = ({message, onRetry}) => (
    <div
        data-testid="df-error"
        role="alert"
        className="flex flex-col items-center justify-center gap-3 rounded-xl border border-risk bg-risk-bg p-6 text-center"
    >
        <p className="text-sm text-risk">
            Could not load delivery funnel. {message}
        </p>
        <button
            type="button"
            onClick={onRetry}
            data-testid="df-retry"
            className="inline-flex items-center gap-2 rounded-full border border-risk bg-surface px-4 py-1.5 text-xs font-bold text-risk hover:bg-risk-bg"
        >
            Retry
        </button>
    </div>
);

interface EmptyStateProps {
    testid: string;
    title: string;
    body?: string;
}

const EmptyState: React.FC<EmptyStateProps> = ({testid, title, body}) => (
    <div
        data-testid={testid}
        className="rounded-xl border border-line bg-ghost-bg p-6 text-center"
    >
        <p className="text-sm font-medium text-ink">{title}</p>
        {body && <p className="mt-1 text-xs text-ink-muted">{body}</p>}
    </div>
);

/* ------------------------------------------------------------------ */
/* Glass-shape Sankey + accompanying widgets (bd be0.5)                */
/* ------------------------------------------------------------------ */

/** Stat-strip cell — uppercase label, large value, sub-line. */
const StatCell: React.FC<{
    testid: string;
    label: string;
    value: string;
    sub: string;
    /** Semantic token class for the value (accent / safe / risk). */
    valueClass?: string;
}> = ({testid, label, value, sub, valueClass = 'text-ink'}) => (
    <div
        data-testid={testid}
        className="rounded-xl border border-line bg-surface px-3.5 py-3"
    >
        <div className="text-xs uppercase tracking-wider text-ink-muted">
            {label}
        </div>
        <div className={`mt-1 text-[22px] font-bold leading-tight ${valueClass}`}>
            {value}
        </div>
        <div className="mt-0.5 text-xs text-ink-muted">{sub}</div>
    </div>
);

const StatStrip: React.FC<{
    strip: DeliveryFunnelStatStrip;
    rangeDays: number;
}> = ({strip, rangeDays}) => {
    // Daily/pacing now come from the backend (bd-be0.8). Fall back to legacy
    // derivation if the API hasn't rolled out yet (defensive — keeps the UI
    // working against an older backend in dev).
    const dailyBudget =
        strip.daily > 0
            ? strip.daily
            : strip.budget > 0
                ? strip.budget / rangeDays
                : 0;
    const pacing =
        strip.pacing > 0
            ? strip.pacing
            : strip.budget > 0
                ? strip.spent / strip.budget
                : 0;
    return (
        <div
            data-testid="df-stat-strip"
            className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-4"
        >
            <StatCell
                testid="df-stat-matched"
                label="Matched URLs"
                value={formatInteger(strip.matchedUrls)}
                sub={`on ${formatInteger(strip.domainCount)} domains · last ${rangeDays} days`}
                valueClass="text-accent"
            />
            <StatCell
                testid="df-stat-rate"
                label="Match rate"
                value={formatPercent(strip.matchRate)}
                sub={`URLs scanned: ${formatInteger(strip.scanCount)}`}
                valueClass="text-safe"
            />
            <StatCell
                testid="df-stat-budget"
                label={`Budget (${rangeDays}d)`}
                value={formatCurrency(strip.budget, 'USD')}
                sub={`${formatCurrency(dailyBudget, 'USD')}/day daily`}
            />
            <StatCell
                testid="df-stat-spent"
                label={`Spent (${rangeDays}d)`}
                value={formatCurrency(strip.spent, 'USD')}
                sub={`${formatPercent(pacing)} pacing`}
                valueClass="text-risk"
            />
        </div>
    );
};

/**
 * Delivery funnel band (page lock 7, deepcontext-pnxk): the vendored
 * FunnelGraph wrapper (`ui/FunnelChart`) draws the horizontal curved gradient
 * with its stage labels outside the shape, and a caption row underneath repeats
 * each stage's compact value + conversion sub-line. The drop annotations below
 * stay aligned to the same four columns.
 */
type FunnelStageId = 's1' | 's2' | 's3' | 's4';

const FUNNEL_LABELS = ['Bid requests', 'Past pre-bid filters', 'Bids placed', 'Won (impressions)'];

const StageCaption: React.FC<{
    testid: string;
    stage: FunnelStageId;
    label: string;
    bigNum: string;
    /**
     * Full-precision label rendered as the native `title` tooltip on the
     * compact stage number — preserves precision when the visible label is
     * compacted (e.g. "770M" → hover shows "772,789,218"). bd be0.9.
     */
    bigNumFull?: string;
    topInfo?: string;
    bottomText: string;
    /** Stage 4 (the won/impressions stage) is differentiated with safe ink. */
    valueClass?: string;
}> = ({testid, stage, label, bigNum, bigNumFull, topInfo, bottomText, valueClass = 'text-accent-active'}) => (
    <div
        data-testid={testid}
        data-stage={stage}
        className="rounded-xl border border-line bg-surface px-2.5 py-2 text-center"
    >
        <div className="inline-flex items-center justify-center text-xs font-semibold text-ink-muted">
            {label}
            {topInfo && (
                <span
                    title={topInfo}
                    className="ml-1 inline-flex h-3.5 w-3.5 cursor-help items-center justify-center rounded-full bg-ghost-bg text-xs font-bold text-ink-muted"
                >
                    i
                </span>
            )}
        </div>
        <div
            data-testid={`${testid}-value`}
            title={bigNumFull}
            className={`mt-0.5 text-[20px] font-extrabold leading-none ${valueClass}`}
        >
            {bigNum}
        </div>
        <div className="mt-1 text-xs font-semibold text-ink-muted">{bottomText}</div>
    </div>
);

const FunnelBand: React.FC<{
    funnel: DeliveryFunnelStage;
    rangeDays: number;
}> = ({funnel, rangeDays}) => {
    const filtersPct =
        funnel.bidRequests > 0
            ? formatPercent(funnel.pastFilters / funnel.bidRequests)
            : '—';
    const bidsPct =
        funnel.pastFilters > 0
            ? formatPercent(funnel.bidsPlaced / funnel.pastFilters)
            : '—';
    const winRatePct =
        funnel.bidsPlaced > 0
            ? formatPercent(funnel.impressions / funnel.bidsPlaced)
            : '—';
    const spendStr = formatCurrency(funnel.spend, 'USD');
    const values = useMemo(
        () => [funnel.bidRequests, funnel.pastFilters, funnel.bidsPlaced, funnel.impressions],
        [funnel.bidRequests, funnel.pastFilters, funnel.bidsPlaced, funnel.impressions],
    );

    return (
        <div data-testid="df-glass-row">
            <div data-testid="df-funnel-chart" className="rounded-xl bg-canvas px-3 py-2">
                <FunnelChart
                    labels={FUNNEL_LABELS}
                    values={values}
                    height={190}
                    ariaLabel="Delivery funnel: bid requests to impressions"
                />
            </div>
            <div className="mt-2 grid grid-cols-4 gap-1">
                <StageCaption
                    testid="df-glass-stage-1"
                    stage="s1"
                    label="Bid requests"
                    bigNum={formatCompactNumber(funnel.bidRequests)}
                    bigNumFull={formatFullNumber(funnel.bidRequests)}
                    topInfo="Across all advertisers on these domains"
                    bottomText={`supply pool, ${rangeDays}d`}
                />
                <StageCaption
                    testid="df-glass-stage-2"
                    stage="s2"
                    label="Past pre-bid filters"
                    bigNum={formatCompactNumber(funnel.pastFilters)}
                    bigNumFull={formatFullNumber(funnel.pastFilters)}
                    bottomText={`${filtersPct} pass`}
                />
                <StageCaption
                    testid="df-glass-stage-3"
                    stage="s3"
                    label="Bids placed"
                    bigNum={formatCompactNumber(funnel.bidsPlaced)}
                    bigNumFull={formatFullNumber(funnel.bidsPlaced)}
                    bottomText={`${bidsPct} of survivors`}
                />
                <StageCaption
                    testid="df-glass-stage-4"
                    stage="s4"
                    label="Won (impressions)"
                    bigNum={formatCompactNumber(funnel.impressions)}
                    bigNumFull={formatFullNumber(funnel.impressions)}
                    bottomText={`${winRatePct} win rate · ${spendStr}`}
                    valueClass="text-safe"
                />
            </div>
        </div>
    );
};

const formatReasons = (reasons: DropReason[]): string =>
    reasons
        .map((r) => `${r.name} ${formatPercent(r.pct / 100)}`)
        .join(' · ');

const DropAnnotations: React.FC<{drops: DeliveryFunnelDrops}> = ({drops}) => {
    const negCompact = (n: number) => `−${formatCompactNumber(n)}`;
    const negFull = (n: number) => `−${formatFullNumber(n)}`;
    return (
        <div data-testid="df-drops" className="mt-1.5 grid grid-cols-4 gap-1">
            {/* Stage 1 has no drop annotation (entry point). */}
            <div aria-hidden />
            <div
                data-testid="df-drop-blockedPreBid"
                className="rounded-b-md border-t-2 border-risk bg-risk-bg px-2.5 py-2 text-sm leading-snug text-ink"
            >
                <span
                    data-testid="df-drop-blockedPreBid-count"
                    className="font-bold text-risk"
                    title={negFull(drops.blockedPreBid.count)}
                >
                    {negCompact(drops.blockedPreBid.count)}
                </span>{' '}
                blocked pre-bid
                {drops.blockedPreBid.reasons.length > 0 && (
                    <div className="mt-0.5 text-xs text-ink-muted">
                        {formatReasons(drops.blockedPreBid.reasons)}
                    </div>
                )}
            </div>
            <div
                data-testid="df-drop-supplyGap"
                className="rounded-b-md border-t-2 border-line bg-ghost-bg px-2.5 py-2 text-sm leading-snug text-ink"
            >
                <span
                    data-testid="df-drop-supplyGap-count"
                    className="font-medium text-ink-muted"
                    title={negFull(drops?.supplyGap?.count ?? 0)}
                >
                    {negCompact(drops?.supplyGap?.count ?? 0)}
                </span>{' '}
                supply gap{' '}
                <span
                    title="Cross-advertiser supply beyond this campaign's bid volume. Not a campaign rejection."
                    className="inline-flex h-3.5 w-3.5 cursor-help items-center justify-center rounded-full text-xs font-bold"
                    style={{background: 'rgba(156,163,175,0.25)', color: '#9ca3af'}}
                >
                    i
                </span>
                {(drops?.supplyGap?.reasons?.length ?? 0) > 0 && (
                    <div className="mt-0.5 text-xs text-ink-muted">
                        {formatReasons(drops?.supplyGap?.reasons ?? [])}
                    </div>
                )}
            </div>
            <div
                data-testid="df-drop-auctionsLost"
                className="rounded-b-md border-t-2 border-risk bg-risk-bg px-2.5 py-2 text-sm leading-snug text-ink"
            >
                <span
                    data-testid="df-drop-auctionsLost-count"
                    className="font-bold text-risk"
                    title={negFull(drops.auctionsLost.count)}
                >
                    {negCompact(drops.auctionsLost.count)}
                </span>{' '}
                auctions lost
                {drops.auctionsLost.reasons.length > 0 && (
                    <div className="mt-0.5 text-xs text-ink-muted">
                        {formatReasons(drops.auctionsLost.reasons)}
                    </div>
                )}
            </div>
        </div>
    );
};

const MatchedUrlsStrip: React.FC<{
    matchedUrls: number;
    domainCount: number;
    domains: DeliveryFunnelDomainRow[];
    rangeDays: number;
    campaignId: number | null;
    campaignName?: string;
    onOpenDomains?: (payload: DeliveryFunnelOpenDomainsPayload) => void;
}> = ({matchedUrls, domainCount, domains, rangeDays, campaignId, campaignName, onOpenDomains}) => {
    const badgeText = `${formatCompactNumber(matchedUrls)} URLs`;
    const badgeFullText = `${formatFullNumber(matchedUrls)} URLs`;
    const linkText = `which ${formatInteger(domainCount)} domains here →`;
    return (
        <div
            data-testid="df-matched-strip"
            className="mt-4 flex items-center gap-3 rounded-md px-4 py-3 text-[12.5px]"
            style={{background: '#F5EFFE'}}
        >
            <span
                data-testid="df-matched-badge"
                title={badgeFullText}
                className="whitespace-nowrap rounded-md px-2.5 py-1 text-[13px] font-bold text-white"
                style={{background: '#8D5DD9'}}
            >
                {badgeText}
            </span>
            <span className="text-ink">
                Your matched URLs live on{' '}
                <b className="text-ink">
                    {formatInteger(domainCount)} of these domains
                </b>
                . We can&apos;t tell per-URL how each one performed — DSP-side
                data aggregates to domain. But you can see{' '}
                {onOpenDomains ? (
                    <button
                        type="button"
                        data-testid="df-matched-link"
                        onClick={() =>
                            onOpenDomains({
                                domains,
                                rangeDays,
                                campaignId,
                                campaignName,
                            })
                        }
                        className="font-semibold underline underline-offset-2"
                        style={{color: '#693BB1'}}
                    >
                        {linkText}
                    </button>
                ) : (
                    <span
                        data-testid="df-matched-link"
                        className="font-semibold underline underline-offset-2"
                        style={{color: '#693BB1'}}
                    >
                        {linkText}
                    </span>
                )}
            </span>
        </div>
    );
};

const FunnelFootnote: React.FC = () => (
    <p
        data-testid="df-footnote"
        className="mt-3.5 text-sm italic leading-relaxed text-ink-muted"
    >
        ⓘ The first two funnel stages (&ldquo;bid requests&rdquo; and
        &ldquo;past pre-bid filters&rdquo;) count inventory across <b>all</b>{' '}
        advertisers on these domains — supply context, not this campaign&apos;s
        exclusive slice. The &ldquo;supply gap&rdquo; drop between
        &ldquo;past pre-bid filters&rdquo; and &ldquo;bids placed&rdquo; is
        likewise cross-advertiser supply beyond this campaign&apos;s bid volume
        — it is <b>not</b> a campaign rejection. Everything from the
        &ldquo;bids placed&rdquo; stage onwards is campaign-specific.
    </p>
);

/* ------------------------------------------------------------------ */
/* Diagnostics (bd bqw.13)                                              */
/* ------------------------------------------------------------------ */

const SEVERITY_STYLES: Record<
    Diagnostic['severity'],
    {border: string; bg: string; pill: string; icon: string; label: string}
> = {
    warn: {
        border: 'border-warn',
        bg: 'bg-warn-bg',
        pill: 'bg-warn-bg text-warn',
        icon: '⚠',
        label: 'Warning',
    },
    info: {
        border: 'border-accent',
        bg: 'bg-accent/10',
        pill: 'bg-accent/15 text-accent-active',
        icon: 'ⓘ',
        label: 'Info',
    },
    error: {
        border: 'border-risk',
        bg: 'bg-risk-bg',
        pill: 'bg-risk-bg text-risk',
        icon: '✖',
        label: 'Error',
    },
};

const DiagnosticCard: React.FC<{diagnostic: Diagnostic}> = ({diagnostic}) => {
    const [expanded, setExpanded] = useState(false);
    const s = SEVERITY_STYLES[diagnostic.severity];
    return (
        <div
            data-testid={`df-diagnostic-${diagnostic.severity}`}
            className={`rounded-md border-l-4 ${s.border} ${s.bg} px-3.5 py-2.5`}
        >
            <div className="flex items-start gap-2.5">
                <span aria-hidden className="mt-0.5 text-sm">{s.icon}</span>
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                        <span
                            className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wider ${s.pill}`}
                        >
                            {s.label}
                        </span>
                        <span className="text-[12px] font-semibold text-ink">
                            {diagnostic.name}
                        </span>
                    </div>
                    <p className="mt-1 text-[12.5px] leading-relaxed text-ink">
                        {diagnostic.message}
                    </p>
                    <button
                        type="button"
                        data-testid="df-diagnostic-toggle"
                        onClick={() => setExpanded((v) => !v)}
                        aria-expanded={expanded}
                        className="mt-1.5 text-xs font-medium text-ink-muted hover:text-ink"
                    >
                        {expanded ? 'Hide details' : 'Show details'}
                    </button>
                    {expanded && (
                        <dl
                            data-testid="df-diagnostic-details"
                            className="mt-1.5 grid grid-cols-2 gap-x-4 gap-y-1 text-sm text-ink-muted"
                        >
                            <dt className="text-ink-muted">Computed value</dt>
                            <dd className="font-mono text-ink">
                                {diagnostic.computedValue.toFixed(1)}
                            </dd>
                            <dt className="text-ink-muted">Threshold</dt>
                            <dd className="font-mono text-ink">
                                {diagnostic.threshold.toFixed(1)}
                            </dd>
                        </dl>
                    )}
                </div>
            </div>
        </div>
    );
};

const DiagnosticsList: React.FC<{diagnostics: Diagnostic[] | undefined}> = ({
    diagnostics,
}) => {
    if (!diagnostics || diagnostics.length === 0) return null;
    return (
        <div
            data-testid="df-diagnostics"
            className="mb-5 space-y-2"
            aria-label="Funnel diagnostics"
        >
            {diagnostics.map((d) => (
                <DiagnosticCard key={d.name} diagnostic={d} />
            ))}
        </div>
    );
};

const PayloadRender: React.FC<{
    payload: DeliveryFunnelPayload;
    rangeDays: number;
    campaignId: number | null;
    campaignName?: string;
    onOpenDomains?: (payload: DeliveryFunnelOpenDomainsPayload) => void;
}> = ({payload, rangeDays, campaignId, campaignName, onOpenDomains}) => (
    <div data-testid="df-payload" className="space-y-0">
        <StatStrip strip={payload.stat_strip} rangeDays={rangeDays} />
        <DiagnosticsList diagnostics={payload.diagnostics} />
        <div
            data-testid="df-sankey-wrap"
            className="rounded-xl border border-line bg-surface px-4 py-4"
        >
            <div className="mb-1 text-[13px] font-semibold text-ink">
                Delivery funnel — bid-stream{' '}
                <span className="font-normal text-ink-muted">
                    (summed over period)
                </span>
            </div>
            <div className="mb-4 text-sm leading-relaxed text-ink-muted">
                On the{' '}
                <b className="text-ink">
                    {formatInteger(payload.stat_strip.domainCount)} domains
                </b>{' '}
                where your matched URLs live, here&apos;s what happened to the
                supply stream. The band narrows at each stage; the cards below
                repeat every stage&apos;s value and what was lost.
            </div>
            <FunnelBand funnel={payload.funnel} rangeDays={rangeDays} />
            <DropAnnotations drops={payload.drops} />
            <MatchedUrlsStrip
                matchedUrls={payload.stat_strip.matchedUrls}
                domainCount={payload.stat_strip.domainCount}
                domains={payload.domains}
                rangeDays={rangeDays}
                campaignId={campaignId}
                campaignName={campaignName}
                onOpenDomains={onOpenDomains}
            />
            <FunnelFootnote />
        </div>
    </div>
);

const DeliveryFunnelSection: React.FC<DeliveryFunnelSectionProps> = ({
    blueprintId,
    isExpanded,
    linkedCampaigns,
    onOpenDomains,
}) => {
    // Default campaign: first linked campaign if present; otherwise null = All.
    const firstCampaignId = useMemo<number | null>(() => {
        if (linkedCampaigns && linkedCampaigns.length > 0) {
            return linkedCampaigns[0].id;
        }
        return null;
    }, [linkedCampaigns]);

    const [campaignId, setCampaignId] = useState<number | null>(firstCampaignId);
    const [rangeDays, setRangeDays] = useState<number>(DEFAULT_RANGE_DAYS);
    const [refreshKey, setRefreshKey] = useState<number>(0);
    const [fetchState, setFetchState] = useState<FetchState>(initialFetchState);

    // Sync default campaign when the parent supplies different linkedCampaigns.
    useEffect(() => {
        setCampaignId(firstCampaignId);
    }, [firstCampaignId]);

    const hasExplicitlyEmptyCampaigns =
        Array.isArray(linkedCampaigns) && linkedCampaigns.length === 0;

    const handleRetry = useCallback(() => {
        setRefreshKey((k) => k + 1);
    }, []);

    useEffect(() => {
        if (!isExpanded) return;
        // Skip fetch when there are explicitly zero campaigns — render empty-state instead.
        if (hasExplicitlyEmptyCampaigns) return;

        let cancelled = false;
        const {from, to} = dateRangeFor(rangeDays);

        setFetchState({status: 'loading', data: null, errorMessage: null});

        promptApi
            .getDeliveryFunnel(blueprintId, {campaignId, from, to})
            .then((data) => {
                if (cancelled) return;
                setFetchState({status: 'success', data, errorMessage: null});
            })
            .catch((err) => {
                if (cancelled) return;
                setFetchState({
                    status: 'error',
                    data: null,
                    errorMessage: extractErrorMessage(err),
                });
            });

        return () => {
            cancelled = true;
        };
    }, [
        isExpanded,
        blueprintId,
        campaignId,
        rangeDays,
        refreshKey,
        hasExplicitlyEmptyCampaigns,
    ]);

    if (!isExpanded) return null;

    return (
        <div
            data-testid="delivery-funnel-section"
            className="rounded-xl border border-line bg-surface p-3.5"
        >
            <div className="mb-3.5 flex flex-wrap items-center gap-2.5">
                <h5 className="text-[13px] font-extrabold text-ink">Delivery Funnel</h5>
                {linkedCampaigns && linkedCampaigns.length > 0 && (
                    <label className="inline-flex items-center gap-1.5 text-xs font-semibold text-ink-muted">
                        <span>Campaign:</span>
                        <select
                            data-testid="df-campaign-select"
                            value={campaignId === null ? 'all' : String(campaignId)}
                            onChange={(e) => {
                                const v = e.target.value;
                                setCampaignId(v === 'all' ? null : Number(v));
                            }}
                            className="rounded-full border border-line bg-surface px-3 py-1.5 text-xs font-semibold text-ink"
                        >
                            <option value="all">
                                All campaigns using this blueprint ({linkedCampaigns.length})
                            </option>
                            {linkedCampaigns.map((c) => (
                                <option key={c.id} value={String(c.id)}>
                                    {c.name}
                                </option>
                            ))}
                        </select>
                    </label>
                )}
                <label className="inline-flex items-center gap-1.5 text-xs font-semibold text-ink-muted">
                    <span>Date range:</span>
                    <select
                        data-testid="df-range-select"
                        value={rangeDays}
                        onChange={(e) => setRangeDays(Number(e.target.value))}
                        className="rounded-full border border-line bg-surface px-3 py-1.5 text-xs font-semibold text-ink"
                    >
                        {RANGE_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                                {o.label}
                            </option>
                        ))}
                    </select>
                </label>
                <button
                    type="button"
                    data-testid="df-refresh"
                    onClick={handleRetry}
                    className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-ghost-bg px-3.5 py-1.5 text-xs font-bold text-ink hover:opacity-80"
                >
                    <span aria-hidden>↻</span>
                    Refresh
                </button>
            </div>

            {hasExplicitlyEmptyCampaigns ? (
                <EmptyState
                    testid="df-empty-campaigns"
                    title="No campaigns are using this blueprint yet."
                    body="Link this blueprint to a campaign to see its delivery funnel."
                />
            ) : fetchState.status === 'loading' || fetchState.status === 'idle' ? (
                <LoadingSkeleton />
            ) : fetchState.status === 'error' ? (
                <ErrorPanel
                    message={fetchState.errorMessage || 'Unknown error'}
                    onRetry={handleRetry}
                />
            ) : fetchState.data && fetchState.data.stat_strip.scanCount === 0 ? (
                <EmptyState
                    testid="df-empty-scans"
                    title="No scans in the selected window."
                    body="The blueprint hasn't scanned any URLs in this date range yet."
                />
            ) : fetchState.data && fetchState.data.stat_strip.matchedUrls === 0 ? (
                <EmptyState
                    testid="df-empty-matched"
                    title="No URLs matched in the selected window."
                    body="Scans ran, but no URLs matched the blueprint."
                />
            ) : fetchState.data ? (
                <PayloadRender
                    payload={fetchState.data}
                    rangeDays={rangeDays}
                    campaignId={campaignId}
                    campaignName={
                        linkedCampaigns?.find((c) => c.id === campaignId)?.name
                    }
                    onOpenDomains={onOpenDomains}
                />
            ) : null}
        </div>
    );
};

export default DeliveryFunnelSection;
